import { Navigate, NavLink, useParams } from "react-router-dom";
import { gameCategories, guideBySlug, parentGuides } from "./content";
import usePageMetadata from "./usePageMetadata";

const gameByPath = Object.fromEntries(
    gameCategories.map((game) => [game.path.replace("/games/", ""), game]),
);

export default function MathGameGuide() {
    const { guideSlug } = useParams();
    const guide = guideBySlug[guideSlug];

    usePageMetadata({
        title: guide?.seoTitle || "Math Games Guide | MathMinds",
        description: guide?.description || "MathMinds parent guide to choosing math games for children.",
        canonicalPath: guide?.path,
    });

    if (!guide) return <Navigate to="/math-games/" replace />;

    const related = parentGuides.filter((item) => item.slug !== guide.slug).slice(0, 3);

    return (
        <article className="mx-auto max-w-[1000px] px-5 py-12 sm:px-8 lg:py-16">
            <nav aria-label="Breadcrumb" className="text-sm text-[#64736D]">
                <NavLink to="/" className="hover:underline">Home</NavLink>
                <span aria-hidden="true"> / </span>
                <NavLink to="/math-games/" className="hover:underline">Math games</NavLink>
                <span aria-hidden="true"> / </span>
                <span>{guide.label}</span>
            </nav>

            <header className="mt-8 max-w-3xl">
                <p className="font-bold uppercase tracking-[0.18em] text-[#C37C00]">Parent guide</p>
                <h1 className="mt-3 font-chango text-4xl leading-tight text-[#327560] sm:text-5xl">
                    {guide.h1}
                </h1>
                <p className="mt-6 text-lg leading-8 text-[#56635E]">{guide.intro}</p>
            </header>

            <section className="mt-12 space-y-5 text-[17px] leading-8 text-[#56635E]">
                <h2 className="font-chango text-2xl text-[#327560]">Choosing useful practice</h2>
                {guide.guidance.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </section>

            <section className="mt-12 rounded-3xl bg-white p-7 shadow-sm sm:p-9">
                <h2 className="font-chango text-2xl text-[#327560]">What children can practice</h2>
                <ul className="mt-6 grid gap-4 sm:grid-cols-2">
                    {guide.skills.map((skill) => (
                        <li key={skill} className="flex gap-3 rounded-2xl bg-[#F3F8F5] p-4 font-semibold">
                            <span aria-hidden="true" className="text-[#F0A900]">●</span>{skill}
                        </li>
                    ))}
                </ul>
            </section>

            <section className="mt-12">
                <h2 className="font-chango text-2xl text-[#327560]">Try these MathMinds games</h2>
                <p className="mt-3 leading-7 text-[#56635E]">Choose a category, then select a game and level that feels challenging without becoming frustrating.</p>
                <p className="mt-3 rounded-2xl bg-[#FFF6D9] p-5 leading-7 text-[#4F5C57]">{guide.activityNote}</p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {guide.games.map((gameSlug) => {
                        const game = gameByPath[gameSlug] || { name: gameSlug, path: `/games/${gameSlug}`, color: "bg-[#DDF5ED]" };
                        return (
                            <NavLink key={gameSlug} to={game.path} className={`${game.color} rounded-3xl p-6 text-lg font-bold transition-transform hover:-translate-y-1`}>
                                {game.name}<span aria-hidden="true" className="float-right">→</span>
                            </NavLink>
                        );
                    })}
                </div>
            </section>

            <section className="mt-12 rounded-3xl border border-[#E9E2CD] p-7 sm:p-9">
                <h2 className="font-chango text-2xl text-[#327560]">A simple practice routine</h2>
                <ol className="mt-5 space-y-4 leading-7 text-[#56635E]">
                    <li><strong className="text-[#283C43]">1. Start short.</strong> Use a brief session and stop while the child is still engaged.</li>
                    <li><strong className="text-[#283C43]">2. Match the level.</strong> Move down when accuracy drops and move up when answers become automatic.</li>
                    <li><strong className="text-[#283C43]">3. Talk about strategies.</strong> Ask how the child found an answer, not only whether it was correct.</li>
                </ol>
            </section>

            <section className="mt-12 grid gap-6 md:grid-cols-2">
                <div className="rounded-3xl bg-white p-7 shadow-sm">
                    <h2 className="font-chango text-2xl text-[#327560]">Practice examples</h2>
                    <ul className="mt-5 list-disc space-y-3 pl-5 leading-7 text-[#56635E]">
                        {guide.examples.map((example) => <li key={example}>{example}</li>)}
                    </ul>
                </div>
                <div className="rounded-3xl bg-[#FFF6D9] p-7">
                    <h2 className="font-chango text-2xl text-[#327560]">A tip for parents</h2>
                    <p className="mt-5 leading-7 text-[#56635E]">{guide.parentTip}</p>
                </div>
            </section>

            <section className="mt-12 rounded-3xl bg-[#DDF5ED] p-7 sm:p-9">
                <h2 className="font-chango text-2xl text-[#327560]">How MathMinds helps</h2>
                <p className="mt-4 leading-7 text-[#56635E]">{guide.howMathMindsHelps}</p>
            </section>

            <section className="mt-12">
                <h2 className="font-chango text-2xl text-[#327560]">Related parent guides</h2>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    {related.map((item) => (
                        <NavLink key={item.path} to={item.path} className="rounded-2xl bg-[#DDF5ED] p-5 font-bold text-[#327560] hover:underline">
                            {item.label}
                        </NavLink>
                    ))}
                </div>
            </section>
        </article>
    );
}
